import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Download,
  Calendar,
  Filter,
  FileSpreadsheet,
  X,
  CheckCircle2,
  AlertCircle,
  Clock,
  Sparkles,
  Layers,
  Loader2,
  TableProperties
} from 'lucide-react';
import { api } from '../../../shared/api/api.js';
import { useAuthStore } from '../../auth/store/useAuthStore.js';

interface ExportCsvModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type DatePreset = 'all' | 'today' | '7days' | '30days' | 'this_month' | 'this_year' | 'custom';

export const ExportCsvModal: React.FC<ExportCsvModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuthStore();

  const [datePreset, setDatePreset] = useState<DatePreset>('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [isExporting, setIsExporting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Fetch books to calculate live match count preview
  const { data: booksData, isLoading: isLoadingBooks } = useQuery({
    queryKey: ['books', 'for-export-preview', user?.id],
    queryFn: () => api.getBooks(),
    enabled: isOpen,
  });

  const allBooks = booksData?.books || [];

  // Handle Preset selection
  const handlePresetChange = (preset: DatePreset) => {
    setDatePreset(preset);
    setErrorMsg(null);

    const now = new Date();
    const todayStr = now.toISOString().slice(0, 10);

    if (preset === 'all') {
      setStartDate('');
      setEndDate('');
    } else if (preset === 'today') {
      setStartDate(todayStr);
      setEndDate(todayStr);
    } else if (preset === '7days') {
      const past = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      setStartDate(past.toISOString().slice(0, 10));
      setEndDate(todayStr);
    } else if (preset === '30days') {
      const past = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      setStartDate(past.toISOString().slice(0, 10));
      setEndDate(todayStr);
    } else if (preset === 'this_month') {
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      setStartDate(firstDay.toISOString().slice(0, 10));
      setEndDate(todayStr);
    } else if (preset === 'this_year') {
      const firstDayOfYear = new Date(now.getFullYear(), 0, 1);
      setStartDate(firstDayOfYear.toISOString().slice(0, 10));
      setEndDate(todayStr);
    }
  };

  // Calculate matching items based on local filters
  const matchingBooks = useMemo(() => {
    return allBooks.filter((b) => {
      // Date filter
      const bookDate = (b.createdAt || '').slice(0, 10);
      if (startDate && bookDate < startDate) return false;
      if (endDate && bookDate > endDate) return false;

      // Status filter
      if (statusFilter !== 'all' && b.status !== statusFilter) return false;

      // Category filter
      if (categoryFilter !== 'all' && b.category !== categoryFilter) return false;

      return true;
    });
  }, [allBooks, startDate, endDate, statusFilter, categoryFilter]);

  // Categories list
  const categoryOptions = useMemo(() => {
    const set = new Set<string>();
    allBooks.forEach((b) => {
      if (b.category) set.add(b.category);
    });
    return Array.from(set).sort();
  }, [allBooks]);

  if (!isOpen) return null;

  const handleExport = async () => {
    setErrorMsg(null);
    setSuccessMsg(null);

    if (startDate && endDate && startDate > endDate) {
      setErrorMsg('Tanggal mulai tidak boleh melebihi tanggal akhir.');
      return;
    }

    setIsExporting(true);
    try {
      await api.downloadCsvDirect({
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        status: statusFilter,
        category: categoryFilter,
      });

      setSuccessMsg('Berkas CSV berhasil diunduh ke perangkat Anda.');
      setTimeout(() => {
        setSuccessMsg(null);
        onClose();
      }, 1500);
    } catch (err: any) {
      setErrorMsg(err.message || 'Gagal mengekspor data CSV.');
    } finally {
      setIsExporting(false);
    }
  };

  const modalContent = (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/70 backdrop-blur-xs p-2 sm:p-4 overflow-y-auto animate-in fade-in duration-150">
      <div className="bg-white rounded-2xl sm:rounded-3xl border border-slate-200 shadow-2xl max-w-xl w-full max-h-[94dvh] sm:max-h-[90vh] overflow-hidden flex flex-col my-auto">
        {/* Modal Header (Fixed / Shrink-0) */}
        <div className="p-4 sm:p-5 bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-indigo-500/20 border border-indigo-400/30 flex items-center justify-center text-indigo-400 shrink-0">
              <FileSpreadsheet className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm sm:text-base font-bold truncate">Ekspor Data Katalog (CSV)</h3>
              <p className="text-[11px] sm:text-xs text-slate-300 truncate">
                Filter rentang tanggal & status naskah
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Tutup modal"
            className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-xl text-slate-400 hover:text-white hover:bg-white/10 transition cursor-pointer shrink-0 ml-2"
          >
            <X className="w-4 h-4 sm:w-5 sm:h-5" />
          </button>
        </div>

        {/* Modal Body (Scrollable) */}
        <div className="p-4 sm:p-6 space-y-4 sm:space-y-5 overflow-y-auto flex-1">
          {/* Preset Buttons */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-indigo-600" />
              <span>Preset Rentang Waktu</span>
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 sm:gap-2">
              {[
                { id: 'all', label: 'Semua Waktu' },
                { id: 'today', label: 'Hari Ini' },
                { id: '7days', label: '7 Hari Terakhir' },
                { id: '30days', label: '30 Hari Terakhir' },
                { id: 'this_month', label: 'Bulan Ini' },
                { id: 'this_year', label: 'Tahun Ini' },
                { id: 'custom', label: 'Kustom Tanggal' },
              ].map((p, pIdx) => (
                <button
                  key={`csv-preset-${p.id}-${pIdx}`}
                  type="button"
                  onClick={() => handlePresetChange(p.id as DatePreset)}
                  className={`px-2.5 py-2 rounded-xl text-xs font-semibold transition cursor-pointer border text-center min-h-[38px] flex items-center justify-center ${
                    datePreset === p.id
                      ? 'bg-indigo-600 text-white border-indigo-600 shadow-xs'
                      : 'bg-slate-50 hover:bg-slate-100 text-slate-700 border-slate-200'
                  }`}
                >
                  <span className="truncate">{p.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Date Range Inputs */}
          <div className="p-3 sm:p-4 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-2.5 sm:space-y-3">
            <div className="flex items-center gap-1.5 text-xs font-bold text-slate-800">
              <Calendar className="w-3.5 h-3.5 text-indigo-600" />
              <span>Pilih Rentang Tanggal Pendaftaran / Terbit</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">
                  Dari Tanggal (Mulai)
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setDatePreset('custom');
                  }}
                  className="w-full px-3 py-2 sm:py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-hidden font-mono min-h-[40px]"
                />
              </div>

              <div>
                <label className="block text-[11px] font-medium text-slate-500 mb-1">
                  Sampai Tanggal (Akhir)
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setDatePreset('custom');
                  }}
                  className="w-full px-3 py-2 sm:py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-hidden font-mono min-h-[40px]"
                />
              </div>
            </div>

            {startDate && endDate && (
              <p className="text-[11px] text-slate-600 flex flex-wrap items-center gap-1 pt-0.5">
                <span className="font-semibold text-slate-700">Periode:</span>
                <span className="font-mono text-indigo-700 font-bold">{startDate}</span> s/d{' '}
                <span className="font-mono text-indigo-700 font-bold">{endDate}</span>
              </p>
            )}
          </div>

          {/* Additional Filters: Status & Category */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 sm:gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center gap-1.5">
                <Filter className="w-3.5 h-3.5 text-indigo-600" />
                <span>Filter Status Naskah</span>
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 sm:py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-hidden min-h-[40px]"
              >
                <option value="all">Semua Status (Seluruh Naskah)</option>
                <option value="disetujui">Hanya Disetujui (Resmi)</option>
                <option value="diajukan">Hanya Diajukan (Review)</option>
                <option value="draft">Hanya Draft</option>
                <option value="diarsipkan">Hanya Diarsipkan</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5 flex items-center gap-1.5">
                <Layers className="w-3.5 h-3.5 text-indigo-600" />
                <span>Filter Kategori</span>
              </label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full px-3 py-2 sm:py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-hidden min-h-[40px]"
              >
                <option value="all">Semua Kategori</option>
                {categoryOptions.map((cat, idx) => (
                  <option key={`${cat}-${idx}`} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Live Preview Summary Box */}
          <div className="p-3 sm:p-3.5 rounded-2xl bg-indigo-50/80 border border-indigo-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 sm:gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-8 h-8 rounded-xl bg-indigo-600 text-white flex items-center justify-center shrink-0 shadow-2xs font-bold">
                <TableProperties className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <span className="text-xs font-bold text-indigo-950 block truncate">
                  Pratinjau Data yang Akan Diunduh
                </span>
                <span className="text-[11px] text-indigo-700 block truncate">
                  Format Excel UTF-8 BOM • 12 Kolom
                </span>
              </div>
            </div>

            <div className="flex sm:flex-col items-center sm:items-end justify-between sm:justify-center border-t sm:border-t-0 border-indigo-100/80 pt-2 sm:pt-0 shrink-0">
              <span className="text-sm sm:text-base font-extrabold text-indigo-950 font-mono">
                {isLoadingBooks ? '...' : `${matchingBooks.length} baris`}
              </span>
              <span className="text-[10px] text-indigo-600 uppercase font-bold tracking-wider">
                dari {allBooks.length} total buku
              </span>
            </div>
          </div>

          {/* Feedback Messages */}
          {errorMsg && (
            <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-xs text-rose-700 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 shrink-0 text-rose-600" />
              <span>{errorMsg}</span>
            </div>
          )}

          {successMsg && (
            <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-xs text-emerald-700 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-600" />
              <span>{successMsg}</span>
            </div>
          )}
        </div>

        {/* Modal Footer (Fixed / Shrink-0) */}
        <div className="p-3.5 sm:p-5 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
          <span className="text-[11px] text-slate-500 text-center sm:text-left order-2 sm:order-1">
            Checksum SHA-256 & kompatibel Excel.
          </span>

          <div className="flex items-center gap-2 w-full sm:w-auto order-1 sm:order-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 sm:flex-none px-4 py-2.5 rounded-xl border border-slate-300 hover:bg-slate-100 text-slate-700 text-xs font-semibold transition cursor-pointer min-h-[42px] flex items-center justify-center"
            >
              Batal
            </button>

            <button
              type="button"
              onClick={handleExport}
              disabled={isExporting || (matchingBooks.length === 0 && !isLoadingBooks)}
              className="flex-1 sm:flex-none px-4 sm:px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white text-xs font-bold transition flex items-center justify-center gap-2 shadow-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed min-h-[42px]"
            >
              {isExporting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Mengekspor...</span>
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  <span className="whitespace-nowrap">Unduh CSV ({matchingBooks.length})</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};